import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  NBA_MY_CLUB_SLUG,
  NBA_MY_CLUB_SLUG_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';

import { DeckEditorDialog } from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { Aside } from '@sorare/gameplay/src/components/profile/Aside';
import { ClubLayout } from '@sorare/gameplay/src/components/profile/ClubLayout';
import {
  Gallery,
  MAX_CARDS_TO_DISPLAY,
} from '@sorare/gameplay/src/components/profile/Gallery';
import { ManagerIDHeader } from '@sorare/gameplay/src/components/profile/ManagerIDHeader';
import {
  FlexGrowBlock,
  Grid,
  Main,
  NewGrid,
} from '@sorare/gameplay/src/components/profile/ui';

import { MyCardsCommonMarket } from 'components/commonMarket/MyCardsCommonMarket';
import { GalleryOnboarding } from 'components/managerProfile/GalleryOnboarding';

import {
  NbaMyClubQuery,
  NbaMyClubQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const NBA_MY_CLUB_QUERY = gql`
  query NbaMyClubQuery(
    $user: String!
    $isLoggedIn: Boolean!
    $sport: Sport!
    $firstCardsToDisplay: Int!
  ) {
    currentUser @include(if: $isLoggedIn) {
      slug
    }
    user(slug: $user) {
      slug
      id
      nbaUserProfile {
        id
        bannerUrl
        clubName
        shieldUrl
        socialPictureFingerprint
        highlightedDeck {
          id
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      cards(
        sport: NBA
        first: $firstCardsToDisplay
        rarities: [unique, super_rare, rare, limited, common]
        inOrderOfRarities: true
      ) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
      cardCollections(startedOnly: true, first: 10, sport: NBA) {
        nodes {
          id
          slug
          userCardCollection(forUserSlug: $user) {
            id
          }
        }
        totalCount
      }

      ...ManagerIDHeader_user
      ...Gallery_publicUserInfoInterface
    }
  }
  ${Gallery.fragments.deck}
  ${Gallery.fragments.anyCard}
  ${Gallery.fragments.publicUserInfoInterface}
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
` as TypedDocumentNode<NbaMyClubQuery, NbaMyClubQueryVariables>;

const MyClub = ({ slug }: { slug: string }) => {
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const {
    flags: { useNewNbaLobby = false },
  } = useFeatureFlags();
  const { currentUser, isMe } = useCurrentUserContext();
  const isCurrentUserPage = isMe(slug);
  const { data, loading } = useQuery(NBA_MY_CLUB_QUERY, {
    variables: {
      user: slug || '',
      isLoggedIn: !!currentUser,
      sport: Sport.NBA,
      firstCardsToDisplay: MAX_CARDS_TO_DISPLAY,
    },
    skip: !slug,
  });
  const { lifecycle } = useLifecycle();

  const [editingListSlug, setEditingListSlug] = useState<string | null>();

  if (loading || !data?.user) {
    return <LoadingIndicator fullHeight />;
  }

  const { cards } = data.user;

  const shareProps = {
    managerProfilePath: generatePath(NBA_MY_CLUB_SLUG, { slug }),
    managerProfileSocialPictures:
      data?.user.nbaUserProfile?.managerProfileSocialPictureUrls,
    galleryPath: generatePath(NBA_MY_CLUB_SLUG_CARDS, { slug }),
    gallerySocialPictures: data?.user.nbaUserProfile?.gallerySocialPictureUrls,
    highlightedDeckPath: `${generatePath(NBA_MY_CLUB_SLUG_CARDS, {
      slug,
    })}?${buildFilterQuery({
      [SEARCH_PARAMS.DECK]: data?.user.nbaUserProfile?.highlightedDeck?.name,
    })}`,
    highlightedDeckSocialPictures:
      data?.user.nbaUserProfile?.highlightedDeck?.socialPictureUrls,
  };

  const blocks = {
    header: (
      <ManagerIDHeader
        user={data.user}
        sport={Sport.NBA}
        userSportProfile={data.user.nbaUserProfile}
        {...shareProps}
      />
    ),
    gallery: (
      <>
        <Gallery
          sport={Sport.NBA}
          cards={cards.nodes}
          onboardingComponent={isCurrentUserPage ? <GalleryOnboarding /> : null}
          shouldShowOnboarding={!lifecycle?.[LIFECYCLE.sawGalleryOnboarding]}
          highlightedDeck={data.user.nbaUserProfile?.highlightedDeck}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          extra={
            !useNewNbaLobby && isCurrentUserPage && <MyCardsCommonMarket />
          }
          user={data.user}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={Sport.NBA}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
  };

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={data?.user.nbaUserProfile?.bannerUrl}>
        <Grid>
          {blocks.header}
          {blocks.gallery}
        </Grid>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout home bannerUrl={data?.user.nbaUserProfile?.bannerUrl}>
      <NewGrid>
        <Main>
          <FlexGrowBlock>{blocks.header}</FlexGrowBlock>
        </Main>
        <Aside>
          <FlexGrowBlock>{blocks.gallery}</FlexGrowBlock>
        </Aside>
      </NewGrid>
    </ClubLayout>
  );
};

const MyClubPage = () => {
  const { slug } = useTypedParams<PageParams>();

  return <MyClub slug={slug} />;
};

export default MyClubPage;
